import React, {useEffect} from 'react';

import {skipToken} from '@reduxjs/toolkit/dist/query';
import moment from 'moment-tz';
import {withRouter} from 'react-router';

import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {useCurrentUserState} from 'app/current-user/api';
import {dataRangeTemplatesMap} from 'app/general/dates/date-range';
import {
  BlankSidebarPage,
  type BlankSidebarPageProps,
} from 'app/general/structures/BlankSidebarPage';
import {useReportRunDetail, useRunReport} from 'app/reports/api';
import {RenderDocumentInstance} from 'app/reports/components/renderers/render-document-taskinstance';
import {getReportRunIDFromURL} from 'app/reports/utils';
import {getTaskReportURL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import type {WithRouterProps} from 'react-router';
import type {
  ProcedureHashidSerializedType,
  ProcessHashidSerializedType,
} from 'sweet-types';

type Props<
  ItemType extends ProcessHashidSerializedType | ProcedureHashidSerializedType
> = WithRouterProps<{
  hashid: string;
  reportid: string;
  versionid: null | string;
}> &
  Omit<BlankSidebarPageProps<ItemType>, 'children'>;

export function BaseDocumentTaskReportPage<
  ItemType extends ProcessHashidSerializedType | ProcedureHashidSerializedType
>({
  params,
  router,
  SideBarComponent,
  useData,
  useDataVersion,
  useDataVersions,
}: Props<ItemType>) {
  const {data: document} = useData(params.hashid);
  const {data: reportDetail, isLoading: isReportDetailLoading} =
    useReportRunDetail(params.reportid ? params.reportid : skipToken);
  const [runReport, {data: reportRun, isLoading: isReportRunLoading}] =
    useRunReport();
  const userTz = useCurrentUserState().data!.timezone;

  const isLoading = isReportRunLoading || isReportDetailLoading;

  useEffect(() => {
    if (!params.reportid && document) {
      // When no report is available; we load a default +7, -30 days interval report.
      const dateRange =
        dataRangeTemplatesMap['next-7-last-30-days'].build(userTz);

      runReport({
        starts_at: dateRange.start,
        ends_at: dateRange.end,
        slug: 'document-taskinstance',
        document_url: document.url,
      });
    }
  }, [document]);

  useEffect(() => {
    if (reportRun?.result?.url != undefined && document) {
      const reportRunID = getReportRunIDFromURL(reportRun.result.url);
      router.push(getTaskReportURL(document.html_url, reportRunID));
    }
  }, [reportRun]);

  const formattedParams =
    reportDetail != null
      ? `${moment(reportDetail.params.starts_at).format('ll')} - ${moment(
          reportDetail.params.ends_at
        ).format('ll')}`
      : null;

  return (
    <BlankSidebarPage
      useData={useData}
      useDataVersion={useDataVersion}
      useDataVersions={useDataVersions}
      SideBarComponent={SideBarComponent}
      params={params}
    >
      {isLoading && (
        <NoResultsMessage
          icon="fa-circle-notch fa-spin"
          head={gt('Generating report...')}
        >
          <p>{gt('Just a moment, your data is on its way...')}</p>
        </NoResultsMessage>
      )}
      {reportDetail != null ? (
        <>
          <div className="d-flex">
            <div className="col-12 col-sm">
              <p className="lead mt-3">{formattedParams}</p>
              <p className="text-muted">
                {gt(
                  'This report covers the next 7 days and last 30 days. For reports covering other date ranges,'
                )}{' '}
                <a
                  href="/accounts/reports/document-taskinstance/"
                  target="_blank"
                >
                  {gt('visit the report page.')}
                </a>
              </p>
            </div>
            <div className="col-auto d-flex align-items-center py-3 py-sm-0">
              <a
                href={`${reportDetail.url}export/`}
                className="btn btn-primary px-3"
              >
                {gt('Download')}
              </a>
            </div>
          </div>
          <RenderDocumentInstance result={reportDetail} />
        </>
      ) : null}
    </BlankSidebarPage>
  );
}

export const DocumentTaskReportPage = withRouter(BaseDocumentTaskReportPage);
